import { useEffect, useRef, useCallback } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];
const LAST_ACTIVITY_KEY = 'wele_last_activity';

/**
 * Auto-logout après inactivité — ROBUSTE face à la veille/arrière-plan.
 *
 * Les `setTimeout` sont GELÉS quand l'onglet passe en arrière-plan ou que l'appareil se met en
 * veille : un timer seul ne déconnecte donc jamais un appareil oublié en lieu public. On persiste
 * l'horodatage de dernière activité et on RE-VÉRIFIE au retour d'onglet (`visibilitychange`) et au
 * `focus` : si le délai est dépassé pendant la veille, on déconnecte immédiatement.
 *
 * @param {object} opts
 * @param {number}   opts.timeout        Fenêtre d'inactivité en ms (défaut 20 min)
 * @param {number}   opts.warningBefore  Avertissement ce nb de ms avant logout (défaut 60 s)
 * @param {Function} opts.onLogout       Appelé au timeout
 * @param {Function} opts.onWarning      Appelé à l'avertissement
 * @param {boolean}  opts.enabled        Désactivé quand pas de session (invité inclus si session)
 */
export function useInactivityTimeout({
  timeout = 20 * 60 * 1000,
  warningBefore = 60 * 1000,
  onLogout,
  onWarning,
  enabled = true,
}) {
  const logoutTimer  = useRef(null);
  const warningTimer = useRef(null);
  const onLogoutRef  = useRef(onLogout);
  const onWarningRef = useRef(onWarning);

  useEffect(() => { onLogoutRef.current  = onLogout;  }, [onLogout]);
  useEffect(() => { onWarningRef.current = onWarning; }, [onWarning]);

  const clear = useCallback(() => {
    clearTimeout(logoutTimer.current);
    clearTimeout(warningTimer.current);
  }, []);

  const reset = useCallback(() => {
    clear();
    if (!enabled) return;
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch (_) { /* quota/private */ }
    warningTimer.current = setTimeout(() => onWarningRef.current?.(), timeout - warningBefore);
    logoutTimer.current  = setTimeout(() => onLogoutRef.current?.(),  timeout);
  }, [clear, enabled, timeout, warningBefore]);

  // Re-vérifie au retour d'onglet / focus (rattrape les timers gelés pendant la veille).
  const checkExpiry = useCallback(() => {
    if (!enabled) return;
    let last = 0;
    try { last = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10); } catch (_) { last = 0; }
    if (last && Date.now() - last >= timeout) onLogoutRef.current?.();
    else reset();
  }, [enabled, timeout, reset]);

  useEffect(() => {
    if (!enabled) { clear(); return; }
    // Au montage : si le délai est déjà dépassé (onglet rouvert après des heures), déconnexion.
    checkExpiry();
    const onVis = () => { if (document.visibilityState === 'visible') checkExpiry(); };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', checkExpiry);
    return () => {
      clear();
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, reset));
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', checkExpiry);
    };
  }, [enabled, reset, clear, checkExpiry]);
}
